import type { EventSummary, EventsClient } from "@project-x/api-clients";
import { createConfiguredEventsClient, fetchPublicEvents } from "@/data/events";
import { appLogger } from "@/lib/observability/logger";
import {
  type EventsFilterState,
  buildListEventsParams,
  serializeFiltersToQuery,
} from "@/utils/eventsFilters";

/** Popular carousel size (ticket revenue order). */
export const LANDING_POPULAR_LIMIT = 5;

/** Grid page size for featured and spotlight sections. */
export const LANDING_PAGE_SIZE = 6;

/** In-process cache TTL for default landing payloads (events change infrequently). */
export const LANDING_CACHE_TTL_MS = 60_000;

/** Translation keys under events.landing.errors.* */
export type LandingErrorCode = "featured" | "grid" | "unexpected";

export type LandingEventsSuccess = {
  ok: true;
  popular: EventSummary[];
  featured: EventSummary[];
  spotlight: EventSummary[];
  totalPages: number;
};

export type LandingEventsFailure = {
  ok: false;
  errorCode: LandingErrorCode;
};

export type LandingEventsResult = LandingEventsSuccess | LandingEventsFailure;

const defaultClient = createConfiguredEventsClient();

/**
 * Builds a stable cache key segment from filter state.
 */
export const landingCacheKey = (filters: EventsFilterState): string =>
  serializeFiltersToQuery(filters) || "default";

/**
 * CDN base URL for canonical AVIF images (e.g. https://img.gruv.fm).
 * Prefer the build-time public var for the static export; fall back to the
 * legacy server var in tests. Empty string disables CDN and uses the gateway
 * `/api/events/.../image` proxy.
 */
const IMAGE_CDN = process.env.NEXT_PUBLIC_IMAGE_CDN_URL || process.env.IMAGE_CDN_URL || "";

/**
 * Returns the image URL for an asset, preferring CDN when the canonical
 * object key is available and IMAGE_CDN_URL is configured.
 */
const resolveAssetImageUrl = (
  eventId: string,
  asset: { id: string; canonical_object_key?: string | null },
): string => {
  if (IMAGE_CDN && asset.canonical_object_key) {
    return `${IMAGE_CDN}/${asset.canonical_object_key}`;
  }
  return `/api/events/${eventId}/assets/${asset.id}/image`;
};

/**
 * Returns the hero image URL for carousel LCP preloading.
 * Prefers CDN URL when canonical_object_key is available.
 */
export const getHeroImageUrl = (event: EventSummary): string | null => {
  if (!event.hero) {
    return null;
  }
  return resolveAssetImageUrl(event.id, event.hero);
};

/**
 * Returns the card image URL (card asset preferred, hero fallback).
 * Prefers CDN URL when canonical_object_key is available.
 */
export const getCardImageUrl = (event: EventSummary): string | null => {
  const asset = event.card ?? event.hero;
  if (!asset) {
    return null;
  }
  return resolveAssetImageUrl(event.id, asset);
};

/**
 * Collects LCP-critical image URLs: first carousel hero and first two grid cards.
 */
export const collectLandingImageUrls = (data: LandingEventsSuccess): string[] => {
  const urls: string[] = [];
  const seen = new Set<string>();

  const add = (url: string | null) => {
    if (url && !seen.has(url)) {
      seen.add(url);
      urls.push(url);
    }
  };

  if (data.popular[0]) {
    add(getHeroImageUrl(data.popular[0]));
  }
  for (const event of data.featured.slice(0, 2)) {
    add(getCardImageUrl(event));
  }

  return urls;
};

/**
 * Fetches popular carousel plus first two grid pages for the events landing.
 */
export const loadLandingEvents = async (
  filters: EventsFilterState,
  client: EventsClient = defaultClient,
): Promise<LandingEventsResult> => {
  try {
    appLogger.info?.("gruv.events.fetch", { stage: "start", filters });

    const [popular, first, second] = await Promise.all([
      fetchPublicEvents(
        buildListEventsParams(filters, {
          orderBy: "ticket_revenue",
          page: 1,
          pageSize: LANDING_POPULAR_LIMIT,
        }),
        client,
      ),
      fetchPublicEvents(
        buildListEventsParams(filters, {
          orderBy: "date",
          page: 1,
          pageSize: LANDING_PAGE_SIZE,
        }),
        client,
      ),
      fetchPublicEvents(
        buildListEventsParams(filters, {
          orderBy: "date",
          page: 2,
          pageSize: LANDING_PAGE_SIZE,
        }),
        client,
      ),
    ]);

    if (!popular.ok) {
      appLogger.error?.("gruv.events.fetch.failed", { area: "popular", detail: popular.error });
      return { ok: false, errorCode: "featured" };
    }

    if (!first.ok || !second.ok) {
      appLogger.error?.("gruv.events.fetch.failed", {
        area: "upcoming",
        first: { ok: first.ok, error: !first.ok ? first.error : undefined },
        second: { ok: second.ok, error: !second.ok ? second.error : undefined },
      });
      return { ok: false, errorCode: "grid" };
    }

    const totalPages = first.data.pagination.total_pages;

    appLogger.info?.("gruv.events.fetch.success", {
      popularCount: popular.data.events.length,
      featuredCount: first.data.events.length,
      spotlightCount: second.data.events.length,
      totalPages,
    });

    return {
      ok: true,
      popular: popular.data.events,
      featured: first.data.events,
      spotlight: second.data.events,
      totalPages,
    };
  } catch (error) {
    appLogger.error?.("gruv.events.fetch.unexpected", { error });
    return { ok: false, errorCode: "unexpected" };
  }
};

/**
 * Server-side cached landing fetch; shared across SSR and prewarm on the same pod.
 */
export const getLandingEventsCached = async (
  filters: EventsFilterState,
  client: EventsClient = defaultClient,
): Promise<LandingEventsResult> => {
  const { cached } = await import("@/lib/cache/server-cache");
  return cached(
    `events:landing:${landingCacheKey(filters)}`,
    () => loadLandingEvents(filters, client),
    LANDING_CACHE_TTL_MS,
  );
};
